import React, { useMemo } from 'react'
import useOfferList from 'hooks/Offers/useOfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import {
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import { EmptyObject } from 'lib/object/objectUtils'

import HeroOfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/HeroOfferCarouselLookingForSomethingElse'
import PaletteSheet from 'LuxKit/PaletteSheet'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import qs from 'qs'
import TextButton from 'LuxKit/Button/TextButton'
import { LFSEOfferType } from 'home/components/LookingForSomethingElse/constants'
import { FormattedMessage } from 'react-intl'

const ExclusiveOfferContainer = styled(PaletteSheet)`
  padding: ${rem(144)} 0 ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  ${mediaQueryUp.tablet} {
    padding: ${rem(96)} 0  ${rem(40)};
  }
`

const TopExclusiveHeaderGrid = styled.div`
  display: grid;
  align-items: center;
  grid-template-areas:
    "title link";
  grid-template-columns: 1fr max-content;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-gap: ${rem(16)};
  }

  > .title-area {
    grid-area: title;
  }

  > .link-area {
    grid-area: link;
  }
`

interface Props {
  filters?: App.OfferListFilters
  random?: boolean
  title?: React.ReactNode
  viewAllUrl?: string
  offerType?: LFSEOfferType
}

function HotelExclusiveOfferCarousel(props: Props) {
  const {
    filters = EmptyObject,
    random,
    title = (
      <FormattedMessage
        id="66v9UyJ8yB"
        defaultMessage="Today's top <i>exclusive</i> offers"
        description="Top picks for me carousel title with italic 'exclusive'"
        values={{
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        }}
      />
    ),
    viewAllUrl,
    offerType = 'hotels',
  } = props
  const offerList = useOfferList(filters, { randomize: random })

  const tracking = useOfferListTracking(getListName('OfferList', 'Hotels', 'Hero Carousel'), { key: getOfferListKey(filters) })

  const mapViewURL = useMemo(() => {
    if (viewAllUrl) {
      return viewAllUrl
    }
    const query = qs.stringify(
      {
        adults: 2,
        children: 'none',
        destinationId: ANYWHERE_PLACE_ID,
        destinationName: ANYWHERE_NAME,
        holidayTypes: 'Limited Time LUX Exclusive',
      },
    )
    return `/search?${query}`
  }, [viewAllUrl])

  return (
    <ExclusiveOfferContainer paletteType="inverse">
      <Group direction="vertical" gap={24} tabletGap={32}>
        <LayoutContainer>
          <TopExclusiveHeaderGrid>
            <Heading className="title-area" variant="heading2" align="start">{title}</Heading>
            <TextButton kind="tertiary" className="link-area" to={mapViewURL}>
              <FormattedMessage
                id="EI2DyR1QGd"
                defaultMessage="View all"
                description="Button text to view all"
              />
            </TextButton>
          </TopExclusiveHeaderGrid>
        </LayoutContainer>
        <OfferListEventsProvider tracking={tracking}>
          <OfferListCarousel
            tileStyle="card-default"
            tileSize="medium"
            overrideOfferList={offerList}
            limit={15}
          >
            <HeroOfferCarouselLookingForSomethingElse
              link={mapViewURL}
              offerType={offerType}
            />
          </OfferListCarousel>
        </OfferListEventsProvider>
      </Group>
    </ExclusiveOfferContainer>
  )
}

export default HotelExclusiveOfferCarousel
